import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
// Import from the slice module directly (not the features/auth barrel): the barrel
// also exports views that import RTK Query endpoints, which import this file — the
// cycle would leave baseApi undefined during module initialization.
import { signOutUser, updateAccessToken } from 'features/auth/store/user.slice';
import type { RootState } from 'shared/store';

const REDUCER_PATH = 'api';
const REFRESH_URL = '/auth/refresh';

type QueryArgs = string | FetchArgs;
type RefreshSessionResponse = { accessToken: string };
type BackofficeBaseQuery = BaseQueryFn<QueryArgs, unknown, FetchBaseQueryError>;
type RawBaseQuery = BaseQueryFn<QueryArgs, unknown, FetchBaseQueryError, Record<string, never>, FetchBaseQueryMeta>;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    headers.set('X-Client-App', 'backoffice');
    if (headers.get('content-type')?.includes('multipart/form-data')) {
      return headers;
    }
    const { user } = getState() as RootState;
    if (user?.accessToken) {
      headers.set('Authorization', `Bearer ${user.accessToken}`);
    }
    if (typeof user?.permissionsHash === 'string') {
      headers.set('X-User-Permissions-Hash', user.permissionsHash);
    }
    return headers;
  },
}) as RawBaseQuery;

const getRequestUrl = (args: QueryArgs): string => (typeof args === 'string' ? args : args.url);
const isAuthRequest = (args: QueryArgs): boolean => getRequestUrl(args).startsWith('/auth');
const isUnauthorizedError = (error: FetchBaseQueryError): boolean =>
  error.status === 401 || (error.status === 'PARSING_ERROR' && error.originalStatus === 401);

const forceSignOut = (api: BaseQueryApi): void => {
  api.dispatch({ type: `${REDUCER_PATH}/resetApiState` });
  api.dispatch(signOutUser());
};

let refreshRequest: Promise<RefreshSessionResponse | null> | null = null;

const refreshSession = (api: BaseQueryApi): Promise<RefreshSessionResponse | null> => {
  if (!refreshRequest) {
    refreshRequest = (async (): Promise<RefreshSessionResponse | null> => {
      const result = await rawBaseQuery({ url: REFRESH_URL, method: 'POST' }, api, {});
      return (result.data as RefreshSessionResponse | undefined) ?? null;
    })().finally((): void => {
      refreshRequest = null;
    });
  }

  return refreshRequest;
};

const baseQueryWithReauth: BackofficeBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (!result.error || !isUnauthorizedError(result.error) || isAuthRequest(args)) {
    return result;
  }

  const refreshedSession = await refreshSession(api);
  if (!refreshedSession?.accessToken) {
    forceSignOut(api);
    return result;
  }

  api.dispatch(updateAccessToken(refreshedSession.accessToken));
  return rawBaseQuery(args, api, extraOptions);
};

let isInvalidatingPermissions = false;

export const baseQueryWithPermissionsCheck: BackofficeBaseQuery = async (args, api, extraOptions) => {
  const result = await baseQueryWithReauth(args, api, extraOptions);
  const response = (result.meta as FetchBaseQueryMeta | undefined)?.response;
  const permissionsChanged = response?.headers.get('X-Permissions-Changed');

  if (permissionsChanged === 'true' && !isInvalidatingPermissions) {
    isInvalidatingPermissions = true;

    api.dispatch({
      type: `${REDUCER_PATH}/invalidateTags`,
      payload: ['UserPermissions'],
    });

    setTimeout(() => {
      isInvalidatingPermissions = false;
    }, 1000);
  }

  return result;
};

const baseApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: baseQueryWithPermissionsCheck,
  tagTypes: [
    'User',
    'UserPermissions',
    'MasterProject',
    'CRM',
    'FeatureFlags',
    'AgentImages',
    'Stock',
    'Subprojects',
    'PartnerWebhooks',
    'Role',
    'ProjectIntegrations',
    'WhatsAppCallingSettings',
    'MondaySupport',
    'Properties',
    'DemoChatMessages',
    'GlobalPromptSections',
    'ProjectPromptSections',
    'Activity',
    'PromptVersions',
    'AgentModelOptions',
    'BusinessRules',
    'MortgageEntityDocuments',
    'AgentTestScenarios',
    'AgentTestRuns',
    'AgentCases',
    'ExternalLinkConfig',
    'Integrations',
    'IntegrationCredentials',
    'IntegrationConfig',
  ] as const,
  endpoints: () => ({}),
});

export default baseApi;
